import type { MouseEvent, PointerEvent } from "react";
import { useCallback, useRef, useState } from "react";

export function useLongPressReorder<T extends string>(onMove: (activeId: T, overId: T) => void, delay = 360) {
  const [activeId, setActiveId] = useState<T | null>(null);
  const [interactionBlocked, setInteractionBlocked] = useState(false);
  const activeRef = useRef<T | null>(null);
  const timerRef = useRef<number | null>(null);
  const blockTimerRef = useRef<number | null>(null);
  const suppressClickRef = useRef(false);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const blockInteraction = useCallback((duration = 420) => {
    suppressClickRef.current = true;
    setInteractionBlocked(true);
    if (blockTimerRef.current !== null) window.clearTimeout(blockTimerRef.current);
    blockTimerRef.current = window.setTimeout(() => {
      suppressClickRef.current = false;
      setInteractionBlocked(false);
      blockTimerRef.current = null;
    }, duration);
  }, []);

  const end = useCallback(() => {
    clearTimer();
    if (activeRef.current) blockInteraction();
    activeRef.current = null;
    setActiveId(null);
  }, [blockInteraction, clearTimer]);

  const bind = useCallback(
    (id: T) => ({
      "data-reorder-id": id,
      onPointerDown: (event: PointerEvent<HTMLElement>) => {
        if (event.button !== 0) return;
        clearTimer();
        timerRef.current = window.setTimeout(() => {
          activeRef.current = id;
          setActiveId(id);
          blockInteraction(900);
        }, delay);
      },
      onPointerEnter: () => {
        const active = activeRef.current;
        if (active && active !== id) onMove(active, id);
      },
      onPointerMove: (event: PointerEvent<HTMLElement>) => {
        const active = activeRef.current;
        if (!active) return;
        const overElement = document.elementFromPoint(event.clientX, event.clientY)?.closest<HTMLElement>("[data-reorder-id]");
        const overId = overElement?.dataset.reorderId as T | undefined;
        if (overId && overId !== active) onMove(active, overId);
      },
      onPointerUp: end,
      onPointerCancel: end,
      onPointerLeave: () => {
        if (!activeRef.current) clearTimer();
      },
      onContextMenu: (event: MouseEvent<HTMLElement>) => {
        if (activeRef.current || suppressClickRef.current) event.preventDefault();
      }
    }),
    [blockInteraction, clearTimer, delay, end, onMove]
  );

  const blockClickIfNeeded = useCallback((event: MouseEvent<HTMLElement>) => {
    if (activeRef.current || suppressClickRef.current) {
      event.preventDefault();
      event.stopPropagation();
    }
  }, []);

  return { activeId, interactionBlocked, bind, blockClickIfNeeded };
}
